import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Button from 'components/atoms/Button';
import { getPlatform } from 'utils/platform';
import { openAndroidAppSettings, canOpenSystemSettings } from 'utils/capacitorHelpers';
import { captureMessage } from '@sentry/react';
import ChromeInstructions from './ChromeInstructions';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #2b143b;
  z-index: 1500;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: env(safe-area-inset-top);
`;

const Content = styled.div`
  width: 100%;
  max-width: 375px;
  padding: 96px 24px 64px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  min-height: 100vh;
  box-sizing: border-box;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const EmojiContainer = styled.div`
  position: relative;
  width: 46px;
  height: 50px;
  margin-bottom: 8px;
`;

const MicrophoneEmoji = styled.span`
  font-size: 40px;
  font-weight: 600;
  line-height: 1.15;
  position: absolute;
  left: 0;
  top: 0;
`;

const ProhibitionEmoji = styled.span`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  position: absolute;
  right: 0;
  bottom: 0;
`;

const Title = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 22px;
  line-height: 1.15;
  text-align: center;
  color: #ffffff;
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.35;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const InstructionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: auto;
`;

const PrimaryButton = styled(Button)`
  background-color: #7400f6;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  padding: 11px 24px 13px 24px;
  min-height: 44px;
  border-radius: 36px;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    background-color: #6200d6;
  }
  
  &:active:not(:disabled) {
    background-color: #5000b6;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: #905cff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  padding: 12px 24px;
  min-height: 44px;
  border-radius: 36px;
  border: none;
  
  &:hover:not(:disabled) {
    background-color: rgba(144, 92, 255, 0.1);
  }
  
  &:active:not(:disabled) {
    background-color: rgba(144, 92, 255, 0.2);
  }
`;

const ArrowIcon = styled.svg`
  width: 18px;
  height: 18px;
  fill: currentColor;
`;

interface MicrophonePermissionRequestProps {
  onDismiss: () => void;
}

const MicrophonePermissionRequest: React.FC<MicrophonePermissionRequestProps> = ({ onDismiss }) => {
  const [platform, setPlatform] = React.useState<'android' | 'chrome' | 'unsupported'>('chrome');
  const [showInstructions, setShowInstructions] = React.useState(false);

  React.useEffect(() => {
    getPlatform().then(setPlatform);
  }, []);

  const handleOpenSettings = async () => {
    if (platform === 'android' && canOpenSystemSettings()) {
      try {
        await openAndroidAppSettings();
      } catch (error) {
        console.error('Failed to open settings:', error);
      }
    } else if (platform === 'chrome') {
      setShowInstructions(true);
    }
  };

  if (showInstructions && platform === 'chrome') {
    return <ChromeInstructions isVisible />;
  }

  if (platform === 'unsupported') {
    captureMessage('Unsupported platform for Microphone Permission Request', {
      level: 'warning',
    });
  }

  return (
    <Container>
      <Content>
        <HeaderSection>
          <EmojiContainer>
            <MicrophoneEmoji>🎙</MicrophoneEmoji>
            <ProhibitionEmoji>🚫</ProhibitionEmoji>
          </EmojiContainer>
          <Title>Leya can't use your microphone</Title>
          <Subtitle>Please give permission by following steps:</Subtitle>
        </HeaderSection>

        <InstructionsSection>
          <PermissionInstructionCard
            step={1}
            text="Tap"
            highlightedText="Open Settings"
          />
          <PermissionInstructionCard
            step={2}
            text="Set microphone access to"
            highlightedText="Allow"
            showIllustration
          />
        </InstructionsSection>

        <ActionsSection>
          <PrimaryButton onClick={handleOpenSettings}>
            Open Settings
            <ArrowIcon viewBox="0 0 18 18">
              <path d="M6.75 4.5L11.25 9L6.75 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </ArrowIcon>
          </PrimaryButton>
          <SecondaryButton onClick={onDismiss}>
            Dismiss
          </SecondaryButton>
        </ActionsSection>
      </Content>
    </Container>
  );
};

export default MicrophonePermissionRequest;

// Separate component for instruction cards
interface PermissionInstructionCardProps {
  step: number;
  text: string;
  highlightedText: string;
  showIllustration?: boolean;
}

const InstructionCard = styled.div`
  background-color: #391f4b;
  border-radius: 20px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InstructionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepBadge = styled.div`
  width: 20px;
  height: 20px;
  background-color: #93c276;
  border: 1px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 1.25;
  color: #ffffff;
`;

const InstructionText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  
  strong {
    color: #ffffff;
    font-weight: 600;
  }
`;

const IllustrationContainer = styled.div`
  width: 100%;
  height: 103.75px;
  background-image: url('/assets/microphone-permission-illustration.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 12px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 27.88px;
    left: 18px;
    right: 18px;
    height: 30.79px;
    border: 2px solid #7400f6;
    border-radius: 6px;
  }
`;

export const PermissionInstructionCard: React.FC<PermissionInstructionCardProps> = ({
  step,
  text,
  highlightedText,
  showIllustration,
}) => {
  return (
    <InstructionCard>
      <InstructionRow>
        <StepBadge>{step}</StepBadge>
        <InstructionText>
          {text} <strong>{highlightedText}</strong>
        </InstructionText>
      </InstructionRow>
      {showIllustration && <IllustrationContainer />}
    </InstructionCard>
  );
};